/**
 * SegmentationStatsOverlay — displays per-slice and 3D HU statistics for the
 * active segmentation contour on the viewport.
 *
 * Performance: per-slice stats are cached so 3D stats are accumulated from
 * cache rather than re-rasterizing every annotated slice on each update.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAtomValue } from "jotai";
import { rasterizePolygon, worldToPixel } from "../../utils/contourUtils";
import { activeSegmentIndexAtom, isSegmentationActiveAtom } from "../../atoms/segmentationAtoms";
import { getCornerstone, getCornerstoneTools } from "../../utils/dicomLoader";

interface SliceStats {
  area_mm2: number;
  mean: number;
  max: number;
  min: number;
  stdDev: number;
  pixelCount: number;
  /** Centroid of the contour in world coordinates [x, y, z]. */
  centroidWorld: [number, number, number];
}

interface VolumeStats {
  volume_mm3: number;
  mean: number;
  axialWidth_mm: number;
  axialHeight_mm: number;
  zExtent_mm: number;
  sliceCount: number;
}

interface SegmentationStatsOverlayProps {
  viewportIdPrefix: string;
  sliceIndex: number;
}

// ─── Per-slice cache ────────────────────────────────────────────────────────
// Keyed by imageId. Stores everything needed to derive 3D stats without
// re-rasterizing old slices.

interface CachedSlice {
  huSum: number;
  huCount: number;
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
  worldZ: number;
}

const _cache = new Map<string, CachedSlice>();

export function clearSliceHUCache(): void {
  _cache.clear();
}

// ─── Viewport lookup (cached per prefix) ────────────────────────────────────

let _vpCache: { prefix: string; vp: any } | null = null;

function findViewport(cornerstone: any, prefix: string): any {
  if (_vpCache?.prefix === prefix) {
    // Verify it's still valid
    try {
      if (_vpCache.vp.element) return _vpCache.vp;
    } catch {
      /* stale */
    }
  }
  const engines: any[] = cornerstone.getRenderingEngines?.() ?? [];
  for (const engine of engines) {
    for (const vp of engine.getViewports?.() ?? []) {
      if (vp.id?.startsWith(prefix)) {
        _vpCache = { prefix, vp };
        return vp;
      }
    }
  }
  return null;
}

// ─── Compute per-slice stats (fresh) + update cache ─────────────────────────

function computeSliceStats(viewportIdPrefix: string, segmentIndex: number): SliceStats | null {
  try {
    const cornerstone = getCornerstone();
    const csTools = getCornerstoneTools();

    const viewport = findViewport(cornerstone, viewportIdPrefix);
    if (!viewport) return null;

    const currentImageId = viewport.getCurrentImageId?.();
    if (!currentImageId) return null;

    const imageData = viewport.getImageData?.();
    if (!imageData?.scalarData || !imageData?.dimensions || !imageData?.imageData) return null;

    const { dimensions, scalarData, spacing, imageData: vtkImageData } = imageData;
    const [cols, rows] = dimensions;
    const pixelArea = (spacing?.[0] ?? 1) * (spacing?.[1] ?? 1);

    // Filter once — only contours on this slice for this segment
    const allAnnotations: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
    const sliceContours = allAnnotations.filter(
      (ann: any) =>
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        ann.metadata?.referencedImageId === currentImageId &&
        ann.data?.segmentation?.segmentIndex === segmentIndex &&
        ann.data?.contour?.polyline?.length >= 3,
    );

    if (sliceContours.length === 0) {
      _cache.delete(currentImageId);
      return null;
    }

    // Rasterize into a bitmap (one byte per pixel, avoids Set overhead)
    const bitmap = new Uint8Array(cols * rows);
    let rasterCount = 0;

    for (const ann of sliceContours) {
      const polyline: number[][] = ann.data.contour.polyline;
      // Same continuous vertices as Save (ENG-250), so the live area matches
      // what is stored.
      const pixelPoints = polyline.map((worldPt) => worldToPixel(vtkImageData, worldPt));
      if (pixelPoints.length < 3) continue;
      const indices = rasterizePolygon(pixelPoints, cols, rows);
      for (const idx of indices) {
        if (!bitmap[idx]) {
          bitmap[idx] = 1;
          rasterCount++;
        }
      }
    }

    if (rasterCount === 0) {
      _cache.delete(currentImageId);
      return null;
    }

    // Single pass: HU stats + centroid + bounding box (for cache)
    let sum = 0,
      sumSq = 0,
      min = Infinity,
      max = -Infinity;
    let centroidRow = 0,
      centroidCol = 0;
    let minRow = rows,
      maxRow = 0,
      minCol = cols,
      maxCol = 0;

    for (let i = 0; i < bitmap.length; i++) {
      if (!bitmap[i]) continue;
      const hu = scalarData[i] as number;
      sum += hu;
      sumSq += hu * hu;
      if (hu < min) min = hu;
      if (hu > max) max = hu;
      const r = (i / cols) | 0;
      const c = i % cols;
      centroidRow += r;
      centroidCol += c;
      if (r < minRow) minRow = r;
      if (r > maxRow) maxRow = r;
      if (c < minCol) minCol = c;
      if (c > maxCol) maxCol = c;
    }

    const mean = sum / rasterCount;
    const variance = sumSq / rasterCount - mean * mean;

    // Cache for 3D accumulation
    let worldZ = 0;
    try {
      const wp = vtkImageData.indexToWorld([0, 0, 0]);
      worldZ = wp?.[2] ?? 0;
    } catch {
      /* ignore */
    }

    _cache.set(currentImageId, {
      huSum: sum,
      huCount: rasterCount,
      minRow,
      maxRow,
      minCol,
      maxCol,
      worldZ,
    });

    // Centroid in world coords
    let centroidWorld: [number, number, number] = [0, 0, 0];
    try {
      const wp = vtkImageData.indexToWorld([
        centroidCol / rasterCount,
        centroidRow / rasterCount,
        0,
      ]);
      centroidWorld = [wp[0], wp[1], wp[2]];
    } catch {
      /* ignore */
    }

    return {
      area_mm2: rasterCount * pixelArea,
      mean,
      max,
      min,
      stdDev: Math.sqrt(Math.max(0, variance)),
      pixelCount: rasterCount,
      centroidWorld,
    };
  } catch (err) {
    console.warn("[SegmentationStatsOverlay] Error computing stats:", err);
    return null;
  }
}

// ─── Derive 3D stats from cache (no rasterization) ─────────────────────────

function compute3DStats(viewportIdPrefix: string, segmentIndex: number): VolumeStats | null {
  if (_cache.size === 0) return null;

  try {
    const cornerstone = getCornerstone();
    const csTools = getCornerstoneTools();
    const viewport = findViewport(cornerstone, viewportIdPrefix);
    if (!viewport) return null;

    const imageData = viewport.getImageData?.();
    if (!imageData?.dimensions) return null;
    const { spacing, imageData: vtkImageData } = imageData;
    const pixelArea = (spacing?.[0] ?? 1) * (spacing?.[1] ?? 1);

    // Also count annotated slices NOT yet cached (user hasn't scrolled there)
    // by scanning annotation metadata for slice count / world Z
    const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
    const annotatedImageIds = new Set<string>();
    const worldZs: number[] = [];

    for (const ann of allAnns) {
      if (
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        ann.data?.segmentation?.segmentIndex === segmentIndex &&
        ann.metadata?.referencedImageId &&
        ann.data?.contour?.polyline?.length >= 3
      ) {
        const imgId = ann.metadata.referencedImageId;
        if (!annotatedImageIds.has(imgId)) {
          annotatedImageIds.add(imgId);
          // Get Z from polyline (always available, no rasterization needed)
          const z = ann.data.contour.polyline[0]?.[2];
          if (z != null) worldZs.push(z);
        }
      }
    }

    if (annotatedImageIds.size === 0) return null;

    // Accumulate from cache
    let totalPixels = 0,
      totalHUSum = 0,
      totalHUCount = 0;
    let globalMinRow = Infinity,
      globalMaxRow = -Infinity;
    let globalMinCol = Infinity,
      globalMaxCol = -Infinity;

    _cache.forEach((entry) => {
      totalPixels += entry.huCount;
      totalHUSum += entry.huSum;
      totalHUCount += entry.huCount;
      if (entry.minRow < globalMinRow) globalMinRow = entry.minRow;
      if (entry.maxRow > globalMaxRow) globalMaxRow = entry.maxRow;
      if (entry.minCol < globalMinCol) globalMinCol = entry.minCol;
      if (entry.maxCol > globalMaxCol) globalMaxCol = entry.maxCol;
    });

    // Z-extent from world coordinates
    let zExtent_mm = 0;
    if (worldZs.length >= 2) {
      zExtent_mm = Math.abs(Math.max(...worldZs) - Math.min(...worldZs));
    }

    // Slice thickness from VTK image data
    let realSliceThickness = 1;
    try {
      const z0 = vtkImageData.indexToWorld([0, 0, 0])?.[2] ?? 0;
      const z1 = vtkImageData.indexToWorld([0, 0, 1])?.[2] ?? 0;
      const dz = Math.abs(z1 - z0);
      if (dz > 0.01) realSliceThickness = dz;
    } catch {
      if (worldZs.length >= 2) {
        const sorted = [...worldZs].sort((a, b) => a - b);
        for (let i = 1; i < sorted.length; i++) {
          const gap = Math.abs(sorted[i] - sorted[i - 1]);
          if (gap > 0.01) {
            realSliceThickness = Math.min(realSliceThickness === 1 ? gap : realSliceThickness, gap);
          }
        }
      }
    }

    const meanHU = totalHUCount > 0 ? totalHUSum / totalHUCount : 0;

    return {
      volume_mm3: totalPixels * pixelArea * realSliceThickness,
      mean: meanHU,
      axialWidth_mm:
        globalMaxCol >= globalMinCol ? (globalMaxCol - globalMinCol + 1) * (spacing?.[0] ?? 1) : 0,
      axialHeight_mm:
        globalMaxRow >= globalMinRow ? (globalMaxRow - globalMinRow + 1) * (spacing?.[1] ?? 1) : 0,
      zExtent_mm,
      sliceCount: annotatedImageIds.size,
    };
  } catch (err) {
    console.warn("[SegmentationStatsOverlay] Error computing 3D stats:", err);
    return null;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export const SegmentationStatsOverlay: React.FC<SegmentationStatsOverlayProps> = ({
  viewportIdPrefix,
  sliceIndex,
}) => {
  const [stats, setStats] = useState<SliceStats | null>(null);
  const [volStats, setVolStats] = useState<VolumeStats | null>(null);
  const isSegmentationActive = useAtomValue(isSegmentationActiveAtom);
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  const sliceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recomputeSlice = useCallback(() => {
    if (!isSegmentationActive) {
      setStats(null);
      return;
    }
    setStats(computeSliceStats(viewportIdPrefix, activeSegmentIndex));
  }, [viewportIdPrefix, activeSegmentIndex, isSegmentationActive]);

  const recompute3D = useCallback(() => {
    if (!isSegmentationActive) {
      setVolStats(null);
      return;
    }
    setVolStats(compute3DStats(viewportIdPrefix, activeSegmentIndex));
  }, [viewportIdPrefix, activeSegmentIndex, isSegmentationActive]);

  // On slice change: debounce per-slice (150ms), defer 3D (500ms)
  useEffect(() => {
    setStats(null);
    if (sliceTimerRef.current) clearTimeout(sliceTimerRef.current);
    sliceTimerRef.current = setTimeout(recomputeSlice, 150);
    if (volTimerRef.current) clearTimeout(volTimerRef.current);
    volTimerRef.current = setTimeout(recompute3D, 500);
    return () => {
      if (sliceTimerRef.current) clearTimeout(sliceTimerRef.current);
      if (volTimerRef.current) clearTimeout(volTimerRef.current);
    };
  }, [sliceIndex, recomputeSlice, recompute3D]);

  // On annotation events: recompute both (debounced)
  useEffect(() => {
    if (!isSegmentationActive) return;
    let target: any = null;
    try {
      target = getCornerstone().eventTarget;
    } catch {
      return;
    }
    if (!target) return;

    const onAnnotationEvent = () => {
      if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
      eventTimerRef.current = setTimeout(() => {
        recomputeSlice();
        recompute3D();
      }, 250);
    };

    const events = [
      "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED",
      "CORNERSTONE_TOOLS_ANNOTATION_MODIFIED",
      "CORNERSTONE_TOOLS_ANNOTATION_REMOVED",
      "CORNERSTONE_TOOLS_ANNOTATION_INTERPOLATION_PROCESS_COMPLETED",
    ];
    for (const evt of events) target.addEventListener(evt, onAnnotationEvent);
    return () => {
      for (const evt of events) target.removeEventListener(evt, onAnnotationEvent);
      if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
    };
  }, [isSegmentationActive, recomputeSlice, recompute3D]);

  if ((!stats && !volStats) || !isSegmentationActive) return null;

  const textStyle = {
    color: "#c084fc",
    textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.6)",
  };
  const boxStyle = {
    border: "1px solid rgba(168, 85, 247, 0.4)",
    borderRadius: 4,
    padding: "4px 6px",
    background: "rgba(0, 0, 0, 0.5)",
  };

  return (
    <div className="absolute inset-0 pointer-events-none select-none z-10">
      <div className="absolute flex items-start gap-2" style={{ top: 8, right: 8 }}>
        {volStats && (
          <div
            className="text-[11px] font-mono leading-relaxed"
            style={{ ...textStyle, ...boxStyle }}
          >
            {volStats.sliceCount > 1 && (
              <div>
                Vol:{" "}
                {volStats.volume_mm3 >= 1000
                  ? `${(volStats.volume_mm3 / 1000).toFixed(1)} cm³`
                  : `${volStats.volume_mm3.toFixed(0)} mm³`}
              </div>
            )}
            <div>
              W×H: {volStats.axialWidth_mm.toFixed(1)}×{volStats.axialHeight_mm.toFixed(1)} mm
            </div>
            {volStats.sliceCount > 1 && (
              <div>
                Z: {volStats.zExtent_mm.toFixed(1)} mm ({volStats.sliceCount} sl)
              </div>
            )}
            <div>Mean: {volStats.mean.toFixed(1)} HU</div>
          </div>
        )}
        {/* 2D per-slice stats — commented out, may relocate later
        {stats && (
          <div className="text-[11px] font-mono leading-relaxed" style={textStyle}>
            <div>Area: {stats.area_mm2.toFixed(0)} mm²</div>
            <div>Mean: {stats.mean.toFixed(1)} HU</div>
            <div>Max: {stats.max.toFixed(0)} HU</div>
            <div>Min: {stats.min.toFixed(0)} HU</div>
            <div>Std Dev: {stats.stdDev.toFixed(1)} HU</div>
          </div>
        )}
        */}
      </div>
    </div>
  );
};
